import { useEffect, useId, useRef, useState } from 'react'
import {
  ANALYTICS_SLIDER_PERIODS,
  analyticsPeriodIdFromIndex,
  analyticsPeriodIndexFromId,
} from '../utils/analyticsPolicy'

export default function AnalyticsPeriodSlider({ value, onCommit, reducedMotion = false }) {
  const listId = useId()
  const liveId = useId()
  const draggingRef = useRef(false)
  const draftRef = useRef(analyticsPeriodIndexFromId(value))
  const onCommitRef = useRef(onCommit)
  const [draft, setDraft] = useState(() => analyticsPeriodIndexFromId(value))
  const [dragging, setDragging] = useState(false)
  const last = ANALYTICS_SLIDER_PERIODS.length - 1

  onCommitRef.current = onCommit
  draftRef.current = draft

  useEffect(() => {
    if (draggingRef.current) return
    setDraft(analyticsPeriodIndexFromId(value))
  }, [value])

  const commitIndex = (index) => {
    const next = analyticsPeriodIdFromIndex(index)
    setDraft(analyticsPeriodIndexFromId(next))
    onCommitRef.current?.(next)
  }

  const finishDrag = () => {
    if (!draggingRef.current) return
    draggingRef.current = false
    setDragging(false)
    commitIndex(draftRef.current)
  }

  useEffect(() => {
    if (!dragging) return undefined
    window.addEventListener('pointerup', finishDrag)
    window.addEventListener('pointercancel', finishDrag)
    return () => {
      window.removeEventListener('pointerup', finishDrag)
      window.removeEventListener('pointercancel', finishDrag)
    }
  }, [dragging])

  const readIndex = (event) => analyticsPeriodIndexFromId(
    analyticsPeriodIdFromIndex(event.currentTarget.value),
  )

  return (
    <div
      className={`analytics-period-slider${dragging ? ' is-dragging' : ''}${reducedMotion ? ' is-static' : ''}`}
      style={{ '--period-progress': String(last === 0 ? 0 : draft / last) }}
    >
      <div className="analytics-period-slider-control">
        <div className="analytics-period-slider-track" aria-hidden="true">
          <span className="analytics-period-slider-fill" />
          <span className="analytics-period-slider-thumb" />
          {ANALYTICS_SLIDER_PERIODS.map((period, index) => (
            <span
              key={period.id}
              className={`analytics-period-slider-mark${draft === index ? ' is-active' : ''}`}
              style={{ left: `${(index / last) * 100}%` }}
            />
          ))}
        </div>
        <input
          type="range"
          className="analytics-period-slider-input"
          min={0}
          max={last}
          step={1}
          value={draft}
          list={listId}
          aria-label="Période affichée"
          aria-describedby={liveId}
          aria-valuemin={0}
          aria-valuemax={last}
          aria-valuenow={draft}
          aria-valuetext={ANALYTICS_SLIDER_PERIODS[draft].label}
          onPointerDown={() => {
            draggingRef.current = true
            setDragging(true)
          }}
          onPointerUp={finishDrag}
          onPointerCancel={finishDrag}
          onInput={(event) => {
            setDraft(readIndex(event))
          }}
          onChange={(event) => {
            const index = readIndex(event)
            setDraft(index)
            if (!draggingRef.current) commitIndex(index)
          }}
        />
        <datalist id={listId}>
          {ANALYTICS_SLIDER_PERIODS.map((period, index) => (
            <option key={period.id} value={index} label={period.label} />
          ))}
        </datalist>
      </div>
      <div className="analytics-period-slider-labels">
        {ANALYTICS_SLIDER_PERIODS.map((period, index) => (
          <button
            key={period.id}
            type="button"
            className={`analytics-period-slider-label${draft === index ? ' is-active' : ''}`}
            tabIndex={-1}
            onClick={() => commitIndex(index)}
          >
            <span className="is-full">{period.label}</span>
            <span className="is-short">{period.short}</span>
          </button>
        ))}
      </div>
      <p id={liveId} className="analytics-period-slider-live">
        Période sélectionnée : {ANALYTICS_SLIDER_PERIODS[draft].label}
      </p>
    </div>
  )
}
